import { createSlice, type PayloadAction, type WithSlice } from '@reduxjs/toolkit';

import {
  rootReducer,
  DATAGRID_INITIAL_PAGE,
  DATAGRID_INITIAL_PAGE_SIZE,
  type User,
} from '@verantos-dev/core-annotation-tool';

import { ADMIN_PROJECT_TABS, AdminProjectTabType } from '../constants';
import { Annotator } from '../types/response';

export interface AdminProjectState {
  selectedTab: AdminProjectTabType;
  defaultAllocationModeEdit: boolean;
  annotators: Annotator[];
  supervisors: User[];
  projectsListPage: number;
  projectsListPageSize: number;
}

const initialState: AdminProjectState = {
  selectedTab: ADMIN_PROJECT_TABS.OVERVIEW,
  defaultAllocationModeEdit: false,
  annotators: [],
  supervisors: [],
  projectsListPage: DATAGRID_INITIAL_PAGE,
  projectsListPageSize: DATAGRID_INITIAL_PAGE_SIZE,
};

const adminProjectSlice = createSlice({
  name: 'adminProject',
  initialState,
  reducers: {
    setSelectedTab: (state, action: PayloadAction<AdminProjectTabType>) => {
      state.selectedTab = action.payload;
    },
    clearSelectedTab: (state) => {
      state.selectedTab = ADMIN_PROJECT_TABS.OVERVIEW;
    },
    setDefaultAllocationModeEdit: (state) => {
      state.defaultAllocationModeEdit = true;
    },
    clearDefaultAllocationModeEdit: (state) => {
      state.defaultAllocationModeEdit = false;
    },
    setAnnotators: (state, action: PayloadAction<Annotator[]>) => {
      state.annotators = action.payload;
    },
    addAnnotator: (state, action: PayloadAction<Annotator>) => {
      if (state.annotators.some((annotator) => annotator.id === action.payload.id)) {
        return;
      }
      state.annotators.push(action.payload);
    },
    removeAnnotator: (state, action: PayloadAction<string>) => {
      if (!state.annotators.some((annotator) => annotator.id === action.payload)) {
        return;
      }
      state.annotators = state.annotators.filter((annotator) => annotator.id !== action.payload);
    },
    setSupervisors: (state, action: PayloadAction<User[]>) => {
      state.supervisors = action.payload;
    },
    addSupervisor: (state, action: PayloadAction<User>) => {
      if (state.supervisors.some((supervisor) => supervisor.id === action.payload.id)) {
        return;
      }
      state.supervisors.push(action.payload);
    },
    removeSupervisor: (state, action: PayloadAction<string>) => {
      if (!state.supervisors.some((supervisor) => supervisor.id === action.payload)) {
        return;
      }
      state.supervisors = state.supervisors.filter(
        (supervisor) => supervisor.id !== action.payload
      );
    },
    handleChangeTrainingPercentage: (
      state,
      action: PayloadAction<{ annotatorId: string; trainingPercentage: number }>
    ) => {
      if (!state.annotators.some((annotator) => annotator.id === action.payload.annotatorId)) {
        return;
      }
      state.annotators = state.annotators.map((annotator) => {
        if (annotator.id === action.payload.annotatorId) {
          const existing = annotator.allocationPercentage;
          return {
            ...annotator,
            allocationPercentage: {
              trainingPercentage: action.payload.trainingPercentage,
              testPercentage: existing?.testPercentage ?? 0,
              validationPercentage: existing?.validationPercentage ?? 0,
            },
          };
        }
        return annotator;
      });
    },
    handleChangeTestPercentage: (
      state,
      action: PayloadAction<{ annotatorId: string; testPercentage: number }>
    ) => {
      if (!state.annotators.some((annotator) => annotator.id === action.payload.annotatorId)) {
        return;
      }
      state.annotators = state.annotators.map((annotator) => {
        if (annotator.id === action.payload.annotatorId) {
          const existing = annotator.allocationPercentage;
          return {
            ...annotator,
            allocationPercentage: {
              trainingPercentage: existing?.trainingPercentage ?? 0,
              testPercentage: action.payload.testPercentage,
              validationPercentage: existing?.validationPercentage ?? 0,
            },
          };
        }
        return annotator;
      });
    },
    handleChangeValidationPercentage: (
      state,
      action: PayloadAction<{ annotatorId: string; validationPercentage: number }>
    ) => {
      if (!state.annotators.some((annotator) => annotator.id === action.payload.annotatorId)) {
        return;
      }
      state.annotators = state.annotators.map((annotator) => {
        if (annotator.id === action.payload.annotatorId) {
          const existing = annotator.allocationPercentage;
          return {
            ...annotator,
            allocationPercentage: {
              trainingPercentage: existing?.trainingPercentage ?? 0,
              testPercentage: existing?.testPercentage ?? 0,
              validationPercentage: action.payload.validationPercentage,
            },
          };
        }
        return annotator;
      });
    },
    setProjectsListPaginationModel: (
      state,
      action: PayloadAction<{ page: number; pageSize: number }>
    ) => {
      state.projectsListPage = action.payload.page;
      state.projectsListPageSize = action.payload.pageSize;
    },
  },
});

export const {
  setSelectedTab,
  clearSelectedTab,
  setDefaultAllocationModeEdit,
  clearDefaultAllocationModeEdit,
  setAnnotators,
  addAnnotator,
  removeAnnotator,
  setSupervisors,
  addSupervisor,
  removeSupervisor,
  handleChangeTrainingPercentage,
  handleChangeTestPercentage,
  handleChangeValidationPercentage,
  setProjectsListPaginationModel,
} = adminProjectSlice.actions;

declare module '@verantos-dev/core-annotation-tool' {
  // WithSlice utility assumes reducer is under slice.reducerPath
  // eslint-disable-next-line @typescript-eslint/no-empty-object-type, @typescript-eslint/no-empty-interface
  export interface LazyLoadedSlices extends WithSlice<typeof adminProjectSlice> {}
}

adminProjectSlice.injectInto(rootReducer);

export { adminProjectSlice };
